import React, { useState, useEffect, ReactNode } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { BaseDialog } from './BaseDialog';
import { TabbedScrollPane } from './TabbedScrollPane';

export interface TabConfig {
  key: string;
  label: string;
  icon: ReactNode;
  content: ReactNode;
}

export type TabbedDialogMode = 'tabs' | 'scroll';

interface TabbedDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string | ReactNode;
  tabs: TabConfig[];
  actions?: ReactNode;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  defaultTab?: string;
  /** Body height in 'tabs' mode. Ignored in 'scroll' mode (which uses 90vh). */
  contentHeight?: string;
  /** Optional content rendered between header and body */
  headerContent?: ReactNode;
  /**
   * 'tabs' (default) — only the active tab's content renders; nav swaps content.
   * 'scroll' — all tabs render in one scrollable pane; nav scroll-jumps and
   * highlights the section currently in view.
   */
  mode?: TabbedDialogMode;
}

const useStyles = makeStyles({
  tabsBody: {
    display: 'flex',
    height: '100%',
    // Stack the nav above the content on mobile so the 200px rail
    // doesn't crush the active tab on a 375px viewport.
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  navTree: {
    width: '200px',
    ...shorthands.borderRight('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('4px'),
    flexShrink: 0,
    overflowY: 'auto',
    '@media (max-width: 768px)': {
      width: '100%',
      boxSizing: 'border-box',
      flexDirection: 'row',
      flexWrap: 'wrap',
      ...shorthands.padding('8px', '12px'),
      ...shorthands.borderRight('0'),
      ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
      flexShrink: 0,
      overflowX: 'auto',
      overflowY: 'visible',
    },
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    ...shorthands.padding('8px', '12px'),
    cursor: 'pointer',
    ...shorthands.borderRadius('4px'),
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground2,
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  navItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontWeight: 600,
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('16px', '24px'),
    minHeight: 0,
  },
});

export const TabbedDialog: React.FC<TabbedDialogProps> = ({
  isOpen,
  onClose,
  title,
  tabs,
  actions,
  width,
  maxWidth = '900px',
  maxHeight = '90vh',
  defaultTab,
  contentHeight = '65vh',
  headerContent,
  mode = 'tabs',
}) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.key || '');

  useEffect(() => {
    if (isOpen && defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [isOpen, defaultTab]);

  const activeTabContent = tabs.find((tab) => tab.key === activeTab)?.content;

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width={width}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      height={mode === 'scroll' ? '90vh' : undefined}
      contentPadding="0"
      actions={actions}
      headerContent={headerContent}
    >
      {mode === 'scroll' ? (
        <TabbedScrollPane tabs={tabs} defaultTab={defaultTab} />
      ) : (
        <div className={styles.tabsBody} style={{ height: contentHeight }}>
          <div className={styles.navTree}>
            {tabs.map((tab) => (
              <div
                key={tab.key}
                className={`${styles.navItem} ${activeTab === tab.key ? styles.navItemActive : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </div>
            ))}
          </div>
          <div className={styles.contentArea}>{activeTabContent}</div>
        </div>
      )}
    </BaseDialog>
  );
};

export default TabbedDialog;
